import React, { useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Modal } from '../common/Modal';
import { getTranslation, mergeTranslations, TranslatedValue, Translations, TranslationEdit } from '../common/TranslatedValue';
import Button from '../common/Button';
import { toast } from 'react-toastify';
import { AddAdVariationTypeValue_CreateVariationTypeValueMutation, AddAdVariationTypeValue_CreateVariationTypeValueMutationVariables, AddAdVariationTypeValue_UpdateVariationTypeValueMutation, AddAdVariationTypeValue_UpdateVariationTypeValueMutationVariables, AddAdVariationTypeValue_DeleteVariationTypeValueMutation, AddAdVariationTypeValue_DeleteVariationTypeValueMutationVariables } from '../../../codegen/graphql';

const CREATE_VARIATION_TYPE_VALUE = gql`
  mutation AddAdVariationTypeValue_CreateVariationTypeValue($input: CreateAdVariationTypeValueInput!) {
    createAdVariationTypeValue(input: $input) {
      id
      value
      variations {
        id
      }
    }
  }
`;

const UPDATE_VARIATION_TYPE_VALUE = gql`
  mutation AddAdVariationTypeValue_UpdateVariationTypeValue($id: ID!, $input: UpdateAdVariationTypeValueInput!) {
    updateAdVariationTypeValue(id: $id, input: $input) {
      id
      value
    }
  }
`;

export const DELETE_VARIATION_TYPE_VALUE = gql`
  mutation AddAdVariationTypeValue_DeleteVariationTypeValue($id: ID!) {
    deleteAdVariationTypeValue(id: $id)
  }
`;


interface AddAdVariationTypeValueProps {
  isOpen: boolean;
  onClose: () => void;
  adId: string;
  typeId: string;
  typeValueId?: string;
  typeValueName?: Translations | string;
  onSuccess?: () => void;
}

export function AddAdVariationTypeValue({ 
  isOpen, 
  onClose, 
  adId, 
  typeId, 
  typeValueId, 
  typeValueName = {}, 
  onSuccess 
}: AddAdVariationTypeValueProps) {
  const initialTranslations: Translations = typeof typeValueName === 'string'
    ? (typeValueName ? { FR: typeValueName } : {})
    : (typeValueName ?? {});
  const [translations, setTranslationsState] = useState<Translations>(initialTranslations);
  const translationsRef = useRef(translations);
  const setTranslations = (next: Translations) => {
    translationsRef.current = next;
    setTranslationsState(next);
  };
  const editTranslations = (updates: TranslationEdit[]) => {
    setTranslations(mergeTranslations(translationsRef.current, updates));
  };
  const [createVariationTypeValue] = useMutation<
    AddAdVariationTypeValue_CreateVariationTypeValueMutation,
    AddAdVariationTypeValue_CreateVariationTypeValueMutationVariables
  >(CREATE_VARIATION_TYPE_VALUE);
  const [updateVariationTypeValue] = useMutation<
    AddAdVariationTypeValue_UpdateVariationTypeValueMutation,
    AddAdVariationTypeValue_UpdateVariationTypeValueMutationVariables
  >(UPDATE_VARIATION_TYPE_VALUE);
  const [deleteVariationTypeValue] = useMutation<
    AddAdVariationTypeValue_DeleteVariationTypeValueMutation,
    AddAdVariationTypeValue_DeleteVariationTypeValueMutationVariables
  >(DELETE_VARIATION_TYPE_VALUE);

  const isEditing = !!typeValueId;

  const handleSubmit = async () => {
    const currentTranslations = translationsRef.current;
    const displayName = getTranslation(currentTranslations);
    if (!displayName.trim()) {
      toast.error('Le nom de la valeur ne peut pas être vide');
      return;
    }

    try {
      if (isEditing && typeValueId) {
        await updateVariationTypeValue({
          variables: {
            id: typeValueId,
            input: {
              value: currentTranslations,
            },
          },
        });
        toast.success(`Valeur "${displayName}" modifiée avec succès`);
      } else {
        await createVariationTypeValue({
          variables: {
            input: {
              typeId,
              value: currentTranslations,
            },
          },
        });
        toast.success(`Valeur "${displayName}" créée avec succès`);
      }
      
      onSuccess?.();
      onClose();
      setTranslations({});
    } catch (error) {
      console.error('Error with variation type value:', error);
      toast.error('Une erreur est survenue');
    }
  };


  const handleDeleteTypeValue = async (typeValueId: string) => {
    try {
      await deleteVariationTypeValue({ variables: { id: typeValueId } });
    } catch (error: any) {
      console.error('Error deleting variation value:', error);
      toast.error('Une erreur est survenue: ' + error.message);
    }
  };


  const handleClose = () => {
    setTranslations(initialTranslations);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Modifier la valeur' : 'Ajouter une valeur'}
      width="small"
    >
      <div className="space-y-4">
        <TranslatedValue
          translations={translations}
          renderAs="input"
          label="Nom de la valeur"
          placeholder="Entrer la valeur"
          autoFocus
          onEditTranslation={editTranslations}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSubmit();
            }
          }}
        />

        {typeValueId && <Button
          preset="danger"
          onClick={() => handleDeleteTypeValue(typeValueId)}
        >
          Supprimer
        </Button>}
        
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleClose}
            preset="white"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
          >
            {isEditing ? 'Modifier' : 'Créer'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
